import React, { FC, useState, useRef } from 'react';
import translate from '@translate';
import ProductsSelect from 'src/components/ProductsSelect';
import { checkFormValidity } from 'src/utils/formFunctions';
import { ReturnProductType } from 'src/types/return';
import TextField from 'src/components/TextField';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import { colors, sizes } from 'src/themes/default';
import Select from 'src/components/Select';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { FormErrorsType } from '../../types/global';
import { getDefaultReturnReason } from './functions';
import { flattenProductCatalogFields } from 'src/utils/productCatalogTableColumns';

export type EditKeysType = 'PRODUCT_ID' | 'NAME' | 'SKU' | 'QUANTITY' | 'RECEIVED' | 'RETURN_REASON';

const getInitData = (skladonUser?: number): ReturnProductType => {
    const defaultReturnReason = getDefaultReturnReason(skladonUser);
    return {
        LINE_STATE: 'E',
        ...(defaultReturnReason ? { RETURN_REASON: defaultReturnReason } : {}),
    };
};

type NewProductBlockPropsType = {
    data?: ReturnProductType[];
    onChange: (value: any) => void;
    disableEdit?: boolean;
    retunReasonCodes: ConstantsCodeType[];
    editErrors: FormErrorsType;
    skladonUser?: number;
};

const NewProductBlock: FC<NewProductBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [editData, setEditData] = useState<ReturnProductType>(() => getInitData(props.skladonUser));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});

    const changeValue = (key: EditKeysType, value: any, error: boolean) => {
        const newData = { ...editData };
        if (key === 'PRODUCT_ID') {
            const catalog = flattenProductCatalogFields(value);
            Object.assign(newData, catalog);
            newData.PRODUCT_ID = value?.ID ?? catalog.PRODUCT_ID;
            newData.NAME = value?.NAME;
            newData.SKU = value?.SKU;
        } else {
            newData[key] = value;
        }
        const newErrors = { ...editErrors };
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const newId = Math.min.apply(0, [0, ...(props.data || []).map((item: any): any => item.ID)]) - 1;

            props.onChange([
                ...(props.data || []),
                {
                    ...editData,
                    ID: newId,
                },
            ]);

            setEditData(getInitData(props.skladonUser));
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <Typography
                gutterBottom
                variant="h3"
                sx={props.editErrors.RETURNED_ORDER_PRODUCTS ? { color: colors.alert_O } : {}}
            >
                {translate('returnDetail.addProduct')}
            </Typography>
            <form ref={formRef}>
                <Grid>
                    <GridItem cols={4}>
                        <ProductsSelect
                            value={editData.PRODUCT_ID}
                            error={editErrors.PRODUCT_ID}
                            idKey="ID"
                            onChange={(value, error) => changeValue('PRODUCT_ID', value, error)}
                            name="PRODUCT_ID"
                            label={translate('returnDetail.productTable.name')}
                            fullWidth
                            disabled={props.disableEdit}
                            required
                            extendsData={['PRODUCT_TYPE', 'EAN']}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <TextField
                            value={editData.QUANTITY}
                            error={editErrors.QUANTITY}
                            onChange={(value, error) => changeValue('QUANTITY', value, error)}
                            name="QUANTITY"
                            label={translate('returnDetail.productTable.quantity')}
                            fullWidth
                            required
                            type="numberLocale"
                            min={1}
                            step="1"
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Select
                            options={props.retunReasonCodes}
                            editableOptions={props.retunReasonCodes.filter(
                                (item) => ['exchange', 'changemind'].indexOf(String(item.value)) < 0
                            )}
                            value={editData.RETURN_REASON}
                            error={editErrors.RETURN_REASON}
                            onChange={(value, error) => changeValue('RETURN_REASON', value, error)}
                            name="RETURN_REASON"
                            label={translate('returnDetail.productTable.returnReason')}
                            fullWidth
                            required
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Button
                            onClick={handleAddRow}
                            disabled={props.disableEdit}
                            sx={{ marginTop: sizes.input_top_PD }}
                            mobileFullWidht
                        >
                            + {translate('returnDetail.productTable.action.add')}
                        </Button>
                    </GridItem>
                </Grid>
            </form>
        </>
    );
};

export default NewProductBlock;
